import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../../components/Layout';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import BopExcelTable, { BopExcelColumn } from '../../components/BopExcelTable';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type TipoOperazione = 'IMPONIBILE' | 'NON_IMPONIBILE' | 'ESENTE' | 'ESCLUSO' | 'FUORI_CAMPO';
type NaturaSDI = 'N1' | 'N2_1' | 'N2_2' | 'N3_1' | 'N3_2' | 'N3_3' | 'N3_4' | 'N3_5' | 'N3_6'
               | 'N4' | 'N5' | 'N6_1' | 'N6_2' | 'N6_3' | 'N6_4' | 'N6_5' | 'N6_6' | 'N6_7' | 'N6_8' | 'N6_9' | 'N7';

interface CodiceIva {
  id: number;
  societaId: number;
  societaNome?: string;
  codice: string;
  descrizione: string;
  aliquota: string;
  tipoOperazione: TipoOperazione;
  naturaSDI: NaturaSDI | null;
  reverseCharge: boolean;
  splitPayment: boolean;
  intraUE: boolean;
  extraUE: boolean;
  note: string | null;
  attivo: boolean;
  predefinito: boolean;
  maiUsato: boolean;
}

interface FormData {
  societaId: number;
  codice: string;
  descrizione: string;
  aliquota: string;
  tipoOperazione: TipoOperazione;
  naturaSDI: NaturaSDI | '';
  reverseCharge: boolean;
  splitPayment: boolean;
  intraUE: boolean;
  extraUE: boolean;
  note: string;
  attivo: boolean;
}

interface ClonaResult {
  creati: { societaId: number; nome: string }[];
  saltati: { societaId: number; nome: string; motivo: string }[];
}

// ── Costanti ──────────────────────────────────────────────────────────────────

const TIPO_INFO: Record<TipoOperazione, { label: string; cssClass: string }> = {
  IMPONIBILE:     { label: 'Imponibile',     cssClass: 'bop-codici-iva__tipo--imponibile' },
  NON_IMPONIBILE: { label: 'Non imponibile', cssClass: 'bop-codici-iva__tipo--non-imponibile' },
  ESENTE:         { label: 'Esente',         cssClass: 'bop-codici-iva__tipo--esente' },
  ESCLUSO:        { label: 'Escluso',        cssClass: 'bop-codici-iva__tipo--escluso' },
  FUORI_CAMPO:    { label: 'Fuori campo',    cssClass: 'bop-codici-iva__tipo--fuori-campo' },
};

const NATURA_LABEL: Record<string, string> = {
  N1:   'N1 — Escluse ex art. 15',
  N2_1: 'N2.1 — Non soggette artt. 7-7septies',
  N2_2: 'N2.2 — Non soggette – altri casi',
  N3_1: 'N3.1 — Non imponibili – esportazioni',
  N3_2: 'N3.2 — Non imponibili – cessioni intracomunitarie',
  N3_3: 'N3.3 — Non imponibili – cessioni verso San Marino',
  N3_4: 'N3.4 — Non imponibili – operazioni assimilate',
  N3_5: "N3.5 — Non imponibili – dichiarazioni d'intento",
  N3_6: 'N3.6 — Non imponibili – altri casi',
  N4:   'N4 — Esenti',
  N5:   'N5 — Regime del margine',
  N6_1: 'N6.1 — RC cessione rottami e recupero',
  N6_2: 'N6.2 — RC cessione oro e argento puro',
  N6_3: 'N6.3 — RC subappalto settore edile',
  N6_4: 'N6.4 — RC cessione fabbricati',
  N6_5: 'N6.5 — RC cessione telefoni cellulari',
  N6_6: 'N6.6 — RC cessione prodotti elettronici',
  N6_7: 'N6.7 — RC prestazioni comparto edile e settori connessi',
  N6_8: 'N6.8 — RC operazioni settore energetico',
  N6_9: 'N6.9 — RC altri casi art. 17 c.6',
  N7:   'N7 — IVA assolta in altro stato UE',
};

const ALL_NATURE = Object.keys(NATURA_LABEL) as NaturaSDI[];

function formatNatura(n: string): string { return n.replace(/_/g, '.'); }

function formatAliquota(a: string | number): string {
  const v = Number(a);
  return v === 0 ? '0%' : `${v}%`;
}

function blankForm(societaId = 0): FormData {
  return {
    societaId,
    codice: '',
    descrizione: '',
    aliquota: '22',
    tipoOperazione: 'IMPONIBILE',
    naturaSDI: '',
    reverseCharge: false,
    splitPayment: false,
    intraUE: false,
    extraUE: false,
    note: '',
    attivo: true,
  };
}

// ── Componente principale ─────────────────────────────────────────────────────

export default function CodiciIvaPage() {
  const [codici, setCodici] = useState<CodiceIva[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ruolo, setRuolo] = useState('UTENTE');

  const {
    societaId: fSocietaId,
    setSocietaId: setFSocietaId,
    listaSocieta: societaList,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // Filtri
  const [fTipo, setFTipo] = useState('');
  const [fRC, setFRC] = useState(false);
  const [fSP, setFSP] = useState(false);
  const [fIntraUE, setFIntraUE] = useState(false);
  const [fExtraUE, setFExtraUE] = useState(false);
  const [fSearch, setFSearch] = useState('');
  const [mostraDisattivi, setMostraDisattivi] = useState(false);

  // Modal form (crea / modifica)
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editItem, setEditItem] = useState<CodiceIva | null>(null);
  const [form, setForm] = useState<FormData>(blankForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Modal visualizza (readonly per codici in uso)
  const [viewItem, setViewItem] = useState<CodiceIva | null>(null);

  // Feedback inizializza predefiniti
  const [initMsg, setInitMsg] = useState('');

  // Modal clona (solo SUPERADMIN)
  const [clonaItem, setClonaItem] = useState<CodiceIva | null>(null);
  const [clonaSelected, setClonaSelected] = useState<number[]>([]);
  const [clonaResult, setClonaResult] = useState<ClonaResult | null>(null);
  const [clonaLoading, setClonaLoading] = useState(false);

  // ── Caricamento ────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!pronta) return;
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (fSocietaId) params.set('societaId', fSocietaId);
      if (fTipo) params.set('tipoOperazione', fTipo);
      if (fRC) params.set('reverseCharge', 'true');
      if (fSP) params.set('splitPayment', 'true');
      if (fIntraUE) params.set('intraUE', 'true');
      if (fExtraUE) params.set('extraUE', 'true');
      if (!mostraDisattivi) params.set('attivo', 'true');
      const data = await apiFetch<{ codici: CodiceIva[] }>(`/api/codici-iva?${params}`);
      setCodici(data.codici);
    } catch (e: unknown) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, [pronta, fSocietaId, fTipo, fRC, fSP, fIntraUE, fExtraUE, mostraDisattivi]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiFetch<{ user: { ruolo: string } }>('/api/protected').then(d => setRuolo(d.user.ruolo)).catch(() => {});
  }, []);

  // ── Azioni ─────────────────────────────────────────────────────────────────

  function openCreate() {
    const sid = fSocietaId ? parseInt(fSocietaId) : (societaList.length === 1 ? societaList[0].id : 0);
    setForm(blankForm(sid));
    setFormError(''); setEditItem(null);
    setModalMode('create'); setModalOpen(true);
  }

  function openEdit(item: CodiceIva) {
    setForm({
      societaId: item.societaId,
      codice: item.codice,
      descrizione: item.descrizione,
      aliquota: String(Number(item.aliquota)),
      tipoOperazione: item.tipoOperazione,
      naturaSDI: item.naturaSDI ?? '',
      reverseCharge: item.reverseCharge,
      splitPayment: item.splitPayment,
      intraUE: item.intraUE,
      extraUE: item.extraUE,
      note: item.note ?? '',
      attivo: item.attivo,
    });
    setFormError(''); setEditItem(item);
    setModalMode('edit'); setModalOpen(true);
  }

  async function handleSave() {
    setFormError('');
    if (!form.codice.trim()) { setFormError('Codice obbligatorio'); return; }
    if (!form.descrizione.trim()) { setFormError('Descrizione obbligatoria'); return; }
    const aliq = parseFloat(form.aliquota);
    if (isNaN(aliq) || aliq < 0) { setFormError('Aliquota non valida'); return; }
    if (form.tipoOperazione !== 'IMPONIBILE' && aliq === 0 && !form.naturaSDI) {
      setFormError('Natura SDI obbligatoria quando aliquota è 0 e tipo non è Imponibile'); return;
    }

    setSaving(true);
    try {
      if (modalMode === 'create') {
        await apiFetch('/api/codici-iva', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            societaId: form.societaId,
            codice: form.codice.toUpperCase().trim(),
            descrizione: form.descrizione.trim(),
            aliquota: aliq,
            tipoOperazione: form.tipoOperazione,
            naturaSDI: form.naturaSDI || null,
            reverseCharge: form.reverseCharge,
            splitPayment: form.splitPayment,
            intraUE: form.intraUE,
            extraUE: form.extraUE,
            note: form.note || null,
          }),
        });
      } else {
        await apiFetch(`/api/codici-iva/${editItem!.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            descrizione: form.descrizione.trim(),
            note: form.note || null,
            attivo: form.attivo,
          }),
        });
      }
      setModalOpen(false);
      await load();
    } catch (e: unknown) { setFormError(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  }

  async function handleDisattiva(id: number) {
    if (!window.confirm('Disattivare questo codice IVA?')) return;
    try {
      await apiFetch(`/api/codici-iva/${id}`, { method: 'DELETE' });
      await load();
    } catch (e: unknown) { setError(e instanceof Error ? e.message : String(e)); }
  }

  async function handleInitializza() {
    const sid = fSocietaId || (societaList.length === 1 ? String(societaList[0].id) : '');
    if (!sid) { setError('Seleziona una società prima di inizializzare'); return; }
    if (!window.confirm('Caricare i codici IVA predefiniti per questa società? I codici già esistenti non verranno sovrascritti.')) return;
    setInitMsg('');
    try {
      const data = await apiFetch<{ ok: boolean; count: number; message: string }>('/api/codici-iva/inizializza-societa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ societaId: parseInt(sid) }),
      });
      setInitMsg(data.message);
      await load();
    } catch (e: unknown) { setError(e instanceof Error ? e.message : String(e)); }
  }

  function openClona(item: CodiceIva) {
    setClonaItem(item);
    setClonaSelected([]);
    setClonaResult(null);
  }

  async function handleClona() {
    if (!clonaItem || clonaSelected.length === 0) return;
    setClonaLoading(true);
    try {
      const data = await apiFetch<ClonaResult>(`/api/codici-iva/${clonaItem.id}/clona`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ societaIds: clonaSelected }),
      });
      setClonaResult(data);
      await load();
    } catch (e: unknown) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setClonaLoading(false); }
  }

  // ── Ricerca client-side ───────────────────────────────────────────────────

  const displayed = fSearch
    ? codici.filter(c =>
        c.codice.toLowerCase().includes(fSearch.toLowerCase()) ||
        c.descrizione.toLowerCase().includes(fSearch.toLowerCase())
      )
    : codici;

  // ── Colonne BopExcelTable ─────────────────────────────────────────────────
  const columns = useMemo<BopExcelColumn<CodiceIva>[]>(() => [
    {
      key: 'codice',
      header: 'Codice',
      accessor: (c) => c.codice,
      filter: { type: 'text' },
      width: '120px',
      render: (item) => (
        <>
          <span className="amministrazione-codice-monospace">{item.codice}</span>
          {item.predefinito && (
            <span className="amministrazione-pill-base amministrazione-pill-std">std</span>
          )}
        </>
      ),
    },
    {
      key: 'descrizione',
      header: 'Descrizione',
      accessor: (c) => c.descrizione,
      filter: { type: 'text' },
      cellClassName: 'amministrazione-descrizione-cell',
      render: (c) => c.descrizione,
    },
    {
      key: 'aliquota',
      header: 'Aliquota',
      accessor: (c) => Number(c.aliquota),
      filter: { type: 'number' },
      width: '110px',
      render: (item) => (
        <span className={`amministrazione-aliquota-text ${Number(item.aliquota) === 0 ? 'amministrazione-aliquota-zero' : 'amministrazione-aliquota-normal'}`}>
          {formatAliquota(item.aliquota)}
        </span>
      ),
    },
    {
      key: 'tipoOperazione',
      header: 'Tipo',
      accessor: (c) => c.tipoOperazione,
      filter: {
        type: 'enum',
        options: (Object.keys(TIPO_INFO) as TipoOperazione[])
          .map(t => ({ value: t, label: TIPO_INFO[t].label })),
      },
      width: '150px',
      render: (item) => {
        const ti = TIPO_INFO[item.tipoOperazione];
        return <span className={`amministrazione-badge-tipo ${ti.cssClass}`}>{ti.label}</span>;
      },
    },
    {
      key: 'naturaSDI',
      header: 'Natura SDI',
      accessor: (c) => c.naturaSDI || '',
      filter: { type: 'text' },
      width: '120px',
      cellClassName: 'amministrazione-natura-text',
      render: (item) => item.naturaSDI ? formatNatura(item.naturaSDI) : '—',
    },
    {
      key: 'flags',
      header: 'Flags',
      accessor: (c) => {
        const parts = [];
        if (c.reverseCharge) parts.push('RC');
        if (c.splitPayment) parts.push('SP');
        if (c.intraUE) parts.push('UE');
        if (c.extraUE) parts.push('XUE');
        return parts.join(' ');
      },
      filter: { type: 'text' },
      width: '150px',
      cellClassName: 'amministrazione-flags-nowrap',
      render: (item) => (
        <>
          {item.reverseCharge && <span className="amministrazione-pill-base amministrazione-pill-rc">RC</span>}
          {item.splitPayment && <span className="amministrazione-pill-base amministrazione-pill-sp">SP</span>}
          {item.intraUE && <span className="amministrazione-pill-base amministrazione-pill-ue">UE</span>}
          {item.extraUE && <span className="amministrazione-pill-base amministrazione-pill-xue">XUE</span>}
          {!item.reverseCharge && !item.splitPayment && !item.intraUE && !item.extraUE && <span className="amministrazione-flags-empty">—</span>}
        </>
      ),
    },
    {
      key: 'attivo',
      header: 'Stato',
      accessor: (c) => c.attivo ? 'ATTIVO' : 'DISATTIVO',
      filter: {
        type: 'enum',
        options: [
          { value: 'ATTIVO',    label: 'Attivo' },
          { value: 'DISATTIVO', label: 'Disattivo' },
        ],
      },
      width: '110px',
      render: (item) => item.attivo
        ? <span className="amministrazione-badge-tipo bop-badge-success">Attivo</span>
        : <span className="amministrazione-badge-tipo bop-badge-neutral">Disattivo</span>,
    },
    {
      key: 'usato',
      header: 'Usato',
      accessor: (c) => c.maiUsato ? 'MAI_USATO' : 'IN_USO',
      filter: {
        type: 'enum',
        options: [
          { value: 'MAI_USATO', label: 'Mai usato' },
          { value: 'IN_USO',    label: 'In uso' },
        ],
      },
      width: '120px',
      render: (item) => item.maiUsato
        ? <span className="amministrazione-badge-tipo bop-badge-success">Mai usato</span>
        : <span className="amministrazione-badge-tipo bop-badge-neutral">🔒 In uso</span>,
    },
  ], []);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout>
      {/* Header */}
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Codici IVA</h2>
          <p>Gestione aliquote e codici IVA per la fatturazione elettronica</p>
        </div>
      </div>

      {error && (
        <div className="inline-error amministrazione-inline-error-mb">{error}</div>
      )}
      {initMsg && (
        <div className="amministrazione-success-message">
          {initMsg}
        </div>
      )}

      <div className="bop-card">
        {/* Filtri */}
        <div className="amministrazione-filter-wrapper amministrazione-inline-error-mb">
          {!isUnica && (
            <select value={fSocietaId} onChange={e => setFSocietaId(e.target.value)} className="bop-select amministrazione-select-auto-width">
              <option value="">Tutte le società</option>
              {societaList.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          )}
          <select value={fTipo} onChange={e => setFTipo(e.target.value)} className="bop-select amministrazione-select-auto-width">
            <option value="">Tutti i tipi</option>
            <option value="IMPONIBILE">Imponibile</option>
            <option value="NON_IMPONIBILE">Non Imponibile</option>
            <option value="ESENTE">Esente</option>
            <option value="ESCLUSO">Escluso</option>
            <option value="FUORI_CAMPO">Fuori Campo</option>
          </select>

          <span className={`amministrazione-toggle-filter ${fRC ? 'amministrazione-toggle-filter-active amministrazione-filter-toggle-rc' : 'amministrazione-toggle-filter-inactive'}`}
                onClick={() => setFRC(!fRC)}>RC</span>
          <span className={`amministrazione-toggle-filter ${fSP ? 'amministrazione-toggle-filter-active amministrazione-filter-toggle-sp' : 'amministrazione-toggle-filter-inactive'}`}
                onClick={() => setFSP(!fSP)}>SP</span>
          <span className={`amministrazione-toggle-filter ${fIntraUE ? 'amministrazione-toggle-filter-active amministrazione-filter-toggle-intra' : 'amministrazione-toggle-filter-inactive'}`}
                onClick={() => setFIntraUE(!fIntraUE)}>IntraUE</span>
          <span className={`amministrazione-toggle-filter ${fExtraUE ? 'amministrazione-toggle-filter-active amministrazione-filter-toggle-extra' : 'amministrazione-toggle-filter-inactive'}`}
                onClick={() => setFExtraUE(!fExtraUE)}>ExtraUE</span>

          <input
            placeholder="Cerca codice o descrizione..."
            value={fSearch}
            onChange={e => setFSearch(e.target.value)}
            className="bop-input amministrazione-search-input"
          />
          <label className="amministrazione-checkbox-label-inline">
            <input type="checkbox" checked={mostraDisattivi} onChange={e => setMostraDisattivi(e.target.checked)} />
            Mostra disattivati
          </label>

          <div className="amministrazione-filter-controls">
            {ruolo === 'SUPERADMIN' && (
              <button
                className="bop-btn bop-btn-secondary"
                onClick={handleInitializza}
                title="Carica i codici IVA standard italiani per questa società"
              >
                Inizializza predefiniti
              </button>
            )}
            <button className="bop-btn bop-btn-primary" onClick={openCreate}>+ Nuovo Codice</button>
          </div>
        </div>

        {/* Tabella Excel-like */}
        <BopExcelTable<CodiceIva>
          data={displayed}
          loading={loading}
          rowKey={(c) => c.id}
          onRowClick={(item) => item.maiUsato ? openEdit(item) : setViewItem(item)}
          initialSort={{ key: 'codice', dir: 'asc' }}
          emptyState={<div className="amministrazione-loading">Nessun codice trovato</div>}
          columns={columns}
          rowActions={(item) => (
            <BopRowActions
              actions={[
                { icon: <RowIcon.Copy />, label: 'Clona', onClick: () => openClona(item), hidden: ruolo !== 'SUPERADMIN' },
                { icon: <RowIcon.Disable />, label: 'Disattiva', onClick: () => handleDisattiva(item.id), variant: 'danger', hidden: !item.attivo },
              ]}
            />
          )}
        />
      </div>

      {/* ── Modal Form (Nuovo / Modifica) ─────────────────────────────────── */}
      {modalOpen && (
        <div className="bop-modal-overlay">
          <div className="bop-modal amministrazione-modal-max-width-620" onClick={e => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">{modalMode === 'create' ? 'Nuovo Codice IVA' : 'Modifica Codice IVA'}</h3>
              <button className="bop-modal__close" onClick={() => setModalOpen(false)}>×</button>
            </div>
            <div className="bop-modal__body amministrazione-modal-body-scroll">
              {formError && <div className="inline-error amministrazione-inline-error-mb">{formError}</div>}

              <div className="amministrazione-modal-grid">
                {/* Società (solo in creazione se multi-società) */}
                {modalMode === 'create' && !isUnica && (
                  <label className="amministrazione-form-label">
                    <span className="amministrazione-form-label-text">Società *</span>
                    <select value={form.societaId} onChange={e => setForm({ ...form, societaId: parseInt(e.target.value) })} className="bop-codici-iva__modal-input">
                      <option value={0}>— Seleziona —</option>
                      {societaList.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                    </select>
                  </label>
                )}

                <div className="amministrazione-modal-grid-2cols">
                  {/* Codice */}
                  <label className="amministrazione-form-label">
                    <span className="amministrazione-form-label-text">Codice * (max 10 car.)</span>
                    {modalMode === 'edit' ? (
                      <div className="bop-codici-iva__modal-input bop-codici-iva__readonly-field">{form.codice}</div>
                    ) : (
                      <input
                        value={form.codice}
                        onChange={e => setForm({ ...form, codice: e.target.value.toUpperCase().slice(0, 10) })}
                        maxLength={10}
                        className="bop-codici-iva__modal-input"
                        placeholder="es. 22, RC63"
                      />
                    )}
                  </label>

                  {/* Aliquota */}
                  <label className="amministrazione-form-label">
                    <span className="amministrazione-form-label-text">Aliquota %</span>
                    {modalMode === 'edit' ? (
                      <div className="bop-codici-iva__modal-input bop-codici-iva__readonly-field">{form.aliquota}%</div>
                    ) : (
                      <input
                        type="number" min="0" max="99.99" step="0.01"
                        value={form.aliquota}
                        onChange={e => setForm({ ...form, aliquota: e.target.value })}
                        className="bop-codici-iva__modal-input"
                      />
                    )}
                  </label>
                </div>

                {/* Descrizione */}
                <label className="amministrazione-form-label">
                  <span className="amministrazione-form-label-text">Descrizione *</span>
                  <input
                    value={form.descrizione}
                    onChange={e => setForm({ ...form, descrizione: e.target.value })}
                    className="bop-codici-iva__modal-input"
                    placeholder="es. IVA ordinaria 22%"
                  />
                </label>

                {/* Tipo Operazione */}
                <label className="amministrazione-form-label">
                  <span className="amministrazione-form-label-text">Tipo Operazione *</span>
                  {modalMode === 'edit' ? (
                    <div className="bop-codici-iva__modal-input bop-codici-iva__readonly-field">
                      {TIPO_INFO[form.tipoOperazione].label}
                    </div>
                  ) : (
                    <select value={form.tipoOperazione} onChange={e => setForm({ ...form, tipoOperazione: e.target.value as TipoOperazione })} className="bop-codici-iva__modal-input">
                      <option value="IMPONIBILE">Imponibile</option>
                      <option value="NON_IMPONIBILE">Non Imponibile</option>
                      <option value="ESENTE">Esente</option>
                      <option value="ESCLUSO">Escluso</option>
                      <option value="FUORI_CAMPO">Fuori Campo</option>
                    </select>
                  )}
                </label>

                {/* Natura SDI */}
                <label className="amministrazione-form-label">
                  <span className="amministrazione-form-label-text">
                    Natura SDI
                    {form.tipoOperazione !== 'IMPONIBILE' && parseFloat(form.aliquota) === 0 && ' *'}
                  </span>
                  {modalMode === 'edit' ? (
                    <div className="bop-codici-iva__modal-input bop-codici-iva__readonly-field">
                      {form.naturaSDI ? NATURA_LABEL[form.naturaSDI] : '—'}
                    </div>
                  ) : (
                    <select value={form.naturaSDI} onChange={e => setForm({ ...form, naturaSDI: e.target.value as NaturaSDI | '' })} className="bop-codici-iva__modal-input">
                      <option value="">— Non specificata —</option>
                      {ALL_NATURE.map(n => <option key={n} value={n}>{NATURA_LABEL[n]}</option>)}
                    </select>
                  )}
                </label>

                {/* Toggle flags */}
                <div className="amministrazione-modal-grid-2cols">
                  {modalMode === 'edit' ? (
                    <>
                      {form.reverseCharge && <div className="bop-codici-iva__pill bop-codici-iva__pill--rc">Reverse Charge</div>}
                      {form.splitPayment && <div className="bop-codici-iva__pill bop-codici-iva__pill--sp">Split Payment</div>}
                      {form.intraUE && <div className="bop-codici-iva__pill bop-codici-iva__pill--ue">IntraUE</div>}
                      {form.extraUE && <div className="bop-codici-iva__pill bop-codici-iva__pill--xue">ExtraUE</div>}
                    </>
                  ) : (
                    <>
                      <span
                        className={`bop-codici-iva__toggle ${form.reverseCharge ? 'bop-codici-iva__toggle--active' : ''}`}
                        onClick={() => setForm({ ...form, reverseCharge: !form.reverseCharge })}
                        title="Il committente versa l'IVA al posto del fornitore"
                      >
                        <span>{form.reverseCharge ? '✓' : '○'}</span> Reverse Charge
                      </span>
                      <span
                        className={`bop-codici-iva__toggle ${form.splitPayment ? 'bop-codici-iva__toggle--active' : ''}`}
                        onClick={() => setForm({ ...form, splitPayment: !form.splitPayment })}
                        title="Per operazioni con Pubblica Amministrazione"
                      >
                        <span>{form.splitPayment ? '✓' : '○'}</span> Split Payment
                      </span>
                      <span
                        className={`bop-codici-iva__toggle ${form.intraUE ? 'bop-codici-iva__toggle--active' : ''}`}
                        onClick={() => setForm({ ...form, intraUE: !form.intraUE })}
                      >
                        <span>{form.intraUE ? '✓' : '○'}</span> Acquisto/Vendita IntraUE
                      </span>
                      <span
                        className={`bop-codici-iva__toggle ${form.extraUE ? 'bop-codici-iva__toggle--active' : ''}`}
                        onClick={() => setForm({ ...form, extraUE: !form.extraUE })}
                      >
                        <span>{form.extraUE ? '✓' : '○'}</span> Acquisto/Vendita ExtraUE
                      </span>
                    </>
                  )}
                </div>

                {/* Note */}
                <label className="amministrazione-form-label">
                  <span className="amministrazione-form-label-text">Note</span>
                  <textarea
                    value={form.note}
                    onChange={e => setForm({ ...form, note: e.target.value })}
                    rows={2}
                    className="bop-codici-iva__modal-input bop-codici-iva__textarea"
                    placeholder="Annotazioni opzionali"
                  />
                </label>

                {/* Attivo (solo modifica) */}
                {modalMode === 'edit' && (
                  <label className="amministrazione-checkbox-label-inline">
                    <input type="checkbox" checked={form.attivo} onChange={e => setForm({ ...form, attivo: e.target.checked })} />
                    <span>Codice attivo</span>
                  </label>
                )}

                {/* Box informativo SDI */}
                <div className="amministrazione-info-box">
                  <p className="amministrazione-info-text">
                    <strong>In fattura elettronica (SDI):</strong>{' '}
                    {form.tipoOperazione === 'IMPONIBILE' || parseFloat(form.aliquota || '0') > 0
                      ? `Aliquota ${form.aliquota || '0'}%`
                      : form.naturaSDI
                        ? `Natura ${formatNatura(form.naturaSDI)} — ${NATURA_LABEL[form.naturaSDI]}`
                        : 'Natura SDI non specificata'
                    }
                  </p>
                </div>
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setModalOpen(false)} disabled={saving}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : modalMode === 'create' ? 'Crea Codice' : 'Salva Modifiche'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Visualizza (readonly, per codici in uso) ─────────────────── */}
      {viewItem && (
        <div className="bop-modal-overlay">
          <div className="bop-modal amministrazione-modal-max-width-560" onClick={e => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Codice IVA — {viewItem.codice}</h3>
              <button className="bop-modal__close" onClick={() => setViewItem(null)}>×</button>
            </div>
            <div className="bop-modal__body">
              <div className="amministrazione-warning-box">
                <p className="amministrazione-warning-text">
                  Questo codice è già stato utilizzato in documenti e non può essere modificato. È possibile solo disattivarlo.
                </p>
              </div>
              <div className="amministrazione-view-grid-2col">
                {[
                  ['Codice', viewItem.codice],
                  ['Aliquota', formatAliquota(viewItem.aliquota)],
                  ['Descrizione', viewItem.descrizione],
                  ['Tipo', TIPO_INFO[viewItem.tipoOperazione].label],
                  ['Natura SDI', viewItem.naturaSDI ? formatNatura(viewItem.naturaSDI) : '—'],
                  ['Note', viewItem.note || '—'],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="amministrazione-field-label">{k}</div>
                    <div className="amministrazione-field-value">{v}</div>
                  </div>
                ))}
              </div>
              <div className="amministrazione-pill-wrapper">
                {viewItem.reverseCharge && <span className="bop-codici-iva__pill bop-codici-iva__pill--rc">Reverse Charge</span>}
                {viewItem.splitPayment && <span className="bop-codici-iva__pill bop-codici-iva__pill--sp">Split Payment</span>}
                {viewItem.intraUE && <span className="bop-codici-iva__pill bop-codici-iva__pill--ue">IntraUE</span>}
                {viewItem.extraUE && <span className="bop-codici-iva__pill bop-codici-iva__pill--xue">ExtraUE</span>}
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setViewItem(null)}>Chiudi</button>
              {viewItem.attivo && (
                <button
                  className="bop-btn bop-codici-iva__btn-disattiva"
                  onClick={() => { handleDisattiva(viewItem.id); setViewItem(null); }}
                >
                  Disattiva Codice
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Clona (solo SUPERADMIN) ─────────────────────────────────── */}
      {clonaItem && (
        <div className="bop-modal-overlay">
          <div className="bop-modal amministrazione-modal-max-width-500" onClick={e => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Clona Codice IVA</h3>
              <button className="bop-modal__close" onClick={() => setClonaItem(null)}>×</button>
            </div>
            <div className="bop-modal__body">
              <div className="amministrazione-inline-error-mb">
                <span className="amministrazione-clona-header-text">Codice da clonare:</span>{' '}
                <strong className="amministrazione-clona-code">{clonaItem.codice}</strong>{' '}
                <span className="amministrazione-clona-desc">{clonaItem.descrizione}</span>
              </div>

              {!clonaResult ? (
                <>
                  <div className="amministrazione-warning-box">
                    <p className="amministrazione-warning-text">
                      I codici già esistenti con lo stesso codice verranno saltati senza sovrascrittura.
                    </p>
                  </div>
                  <div className="amministrazione-clona-btn-group">
                    <button className="bop-btn bop-btn-ghost amministrazione-clona-btn-text" onClick={() => setClonaSelected(societaList.filter(s => s.id !== clonaItem.societaId).map(s => s.id))}>
                      Seleziona tutto
                    </button>
                    <button className="bop-btn bop-btn-ghost amministrazione-clona-btn-text" onClick={() => setClonaSelected([])}>
                      Deseleziona tutto
                    </button>
                  </div>
                  <div className="amministrazione-clona-selection-wrapper">
                    {societaList.filter(s => s.id !== clonaItem.societaId).map(s => (
                      <label key={s.id} className="amministrazione-checkbox-label-inline">
                        <input
                          type="checkbox"
                          checked={clonaSelected.includes(s.id)}
                          onChange={e => setClonaSelected(e.target.checked ? [...clonaSelected, s.id] : clonaSelected.filter(x => x !== s.id))}
                        />
                        {s.nome}
                      </label>
                    ))}
                  </div>
                </>
              ) : (
                <div className="amministrazione-clona-result">
                  {clonaResult.creati.length > 0 && (
                    <div>
                      <p className="amministrazione-clona-success-title">
                        Creati in {clonaResult.creati.length} {clonaResult.creati.length === 1 ? 'società' : 'società'}:
                      </p>
                      {clonaResult.creati.map(c => <div key={c.societaId} className="amministrazione-clona-item">- {c.nome}</div>)}
                    </div>
                  )}
                  {clonaResult.saltati.length > 0 && (
                    <div>
                      <p className="amministrazione-clona-warning-title">
                        Saltati in {clonaResult.saltati.length} {clonaResult.saltati.length === 1 ? 'società' : 'società'}:
                      </p>
                      {clonaResult.saltati.map(c => <div key={c.societaId} className="amministrazione-clona-item">- {c.nome} — {c.motivo}</div>)}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setClonaItem(null)}>Chiudi</button>
              {!clonaResult && (
                <button
                  className="bop-btn bop-btn-primary"
                  onClick={handleClona}
                  disabled={clonaSelected.length === 0 || clonaLoading}
                >
                  {clonaLoading ? 'Clonazione...' : `Clona su ${clonaSelected.length} società`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
